import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { signUp } from '@/lib/authService'
import { routes } from '@/lib/appConfig'
import { getAuthErrorMessage } from '@/shared/components/auth/authErrors'
import { createInitialProfile } from '@/modules/profile'
import { formatPhone, validatePhone } from '@/lib/utils/deviceDetection'
import { AuthLayout } from '@/shared/components/auth'
import { Button, Alert } from '@/shared/components/ui'
import { UserIcon, EmailIcon, LockIcon, EyeIcon, EyeOffIcon, PhoneIcon, FacebookIcon, InstagramIcon, TikTokIcon } from '@/shared/components/ui/Icons'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { useTheme } from '@/shared/contexts/ThemeContext'

const getInputClasses = (isDark: boolean) => {
  const base = `block w-full rounded-lg border-0 py-3 shadow-sm ring-1 ring-inset focus:ring-2 sm:text-sm ${
    isDark
      ? 'bg-slate-700 text-slate-100 ring-slate-600 placeholder:text-slate-400 focus:ring-emerald-500'
      : 'bg-white text-gray-900 ring-gray-300 placeholder:text-gray-400 focus:ring-green-600'
  }`
  return base
}

// Validação de força de senha
interface PasswordRequirements {
  minLength: boolean
  hasUpperCase: boolean
  hasNumber: boolean
  hasSpecialChar: boolean
}

function validatePasswordRequirements(password: string): PasswordRequirements {
  return {
    minLength: password.length >= 6,
    hasUpperCase: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  }
}

function getPasswordStrength(password: string): 'weak' | 'fair' | 'good' | 'strong' {
  if (!password) return 'weak'
  const requirements = validatePasswordRequirements(password)
  const metRequirements = Object.values(requirements).filter(Boolean).length
  
  if (metRequirements <= 1) return 'weak'
  if (metRequirements === 2) return 'fair'
  if (metRequirements === 3) return 'good'
  return 'strong'
}

export default function Signup() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  
  const INPUT_BASE_CLASS = getInputClasses(isDark)
  const INPUT_WITH_ICONS_CLASS = `${INPUT_BASE_CLASS} pl-10 pr-3`
  const INPUT_WITH_BOTH_ICONS_CLASS = `${INPUT_BASE_CLASS} pl-10 pr-10`
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)

  const passwordRequirements = validatePasswordRequirements(password)
  const passwordStrength = getPasswordStrength(password)

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      if (!fullName.trim()) throw new Error('auth.errorNameRequired')
      if (!validatePhone(phone)) throw new Error('auth.errorPhoneInvalid')
      if (password !== confirmPassword) throw new Error('auth.errorPasswordMismatch')
      if (password.length < 6) throw new Error('auth.errorPasswordLength')

      const { data, error } = await signUp(email, password, {
        emailRedirectTo: `${window.location.origin}/login`,
        data: { full_name: fullName.trim(), phone: formatPhone(phone) }
      })

      if (error) throw error

      if (data.user) {
        await createInitialProfile(data.user.id, fullName.trim(), formatPhone(phone), undefined, undefined, undefined, email)
      }

      setIsSuccess(true)
      setTimeout(() => navigate(routes.login), 2000)
    } catch (error: any) {
      setError(getAuthErrorMessage(error.message, t))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout subtitle={t('auth.signupSubtitle')}>
      <form onSubmit={handleSignup} className='space-y-4'>
        {error && <Alert variant='error'>{error}</Alert>}
        {isSuccess && <Alert variant='success'>{t('auth.successRedirecting')}</Alert>}
        
        {/* Nome Completo */}
        <div>
          <label htmlFor="fullName" className={`mb-2 block text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
            {t('auth.fullName')}
          </label>
          <div className='relative'>
            <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
              <UserIcon />
            </div>
            <input 
              id="fullName"
              type='text' 
              placeholder={t('auth.fullNamePlaceholder')} 
              value={fullName} 
              onChange={(e) => setFullName(e.target.value)} 
              className={INPUT_WITH_ICONS_CLASS} 
              disabled={isLoading}
              required
            />
          </div>
        </div>

        {/* Telefone */}
        <div>
          <label htmlFor="phone" className={`mb-2 block text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
            {t('auth.phone')}
          </label>
          <div className='relative'>
            <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
              <PhoneIcon />
            </div>
            <input 
              id="phone"
              type='tel' 
              placeholder={t('auth.phonePlaceholder')} 
              value={phone} 
              onChange={(e) => setPhone(formatPhone(e.target.value))} 
              className={INPUT_WITH_ICONS_CLASS} 
              disabled={isLoading}
              required
            />
          </div>
          <p className={`mt-1 text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            {t('auth.phoneHelp')}
          </p>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className={`mb-2 block text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
            {t('auth.email')}
          </label>
          <div className='relative'>
            <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
              <EmailIcon />
            </div>
            <input 
              id="email"
              type='email' 
              placeholder={t('auth.emailPlaceholder')} 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              className={INPUT_WITH_ICONS_CLASS} 
              disabled={isLoading}
              required
            />
          </div>
        </div>

        {/* Senha */}
        <div>
          <label htmlFor="password" className={`mb-2 block text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
            {t('auth.password')}
          </label>
          <div className='relative'>
            <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
              <LockIcon />
            </div>
            <input 
              id="password"
              type={showPassword ? 'text' : 'password'} 
              placeholder={t('auth.passwordPlaceholder')} 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              className={INPUT_WITH_BOTH_ICONS_CLASS} 
              disabled={isLoading}
              required
            />
            <button
              type='button'
              className='absolute inset-y-0 right-0 flex items-center pr-3'
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
            >
              {showPassword ? (
                <EyeOffIcon className={`h-5 w-5 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
              ) : (
                <EyeIcon className={`h-5 w-5 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
              )}
            </button>
          </div>

          {/* Indicador de Força da Senha */}
          {password && (
            <div className='mt-3 space-y-2'>
              {/* Barra de força */}
              <div className='flex items-center gap-2'>
                <div className='flex-1 h-2 bg-gray-300 dark:bg-slate-600 rounded-full overflow-hidden'>
                  <div
                    className={`h-full transition-all ${
                      passwordStrength === 'weak' ? 'w-1/4 bg-red-500' :
                      passwordStrength === 'fair' ? 'w-1/2 bg-yellow-500' :
                      passwordStrength === 'good' ? 'w-3/4 bg-blue-500' :
                      'w-full bg-green-500'
                    }`}
                  />
                </div>
                <span className={`text-xs font-medium ${
                  passwordStrength === 'weak' ? 'text-red-500' :
                  passwordStrength === 'fair' ? 'text-yellow-500' :
                  passwordStrength === 'good' ? 'text-blue-500' :
                  'text-green-500'
                }`}>
                  {t(`auth.passwordStrength${passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}`)}
                </span>
              </div>

              {/* Requisitos */}
              <div className={`text-xs space-y-1 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                <p className='font-medium'>{t('auth.passwordRequirements')}:</p>
                <div className={`flex items-center gap-2 ${passwordRequirements.minLength ? (isDark ? 'text-emerald-400' : 'text-green-600') : ''}`}>
                  <span>{passwordRequirements.minLength ? '?' : '?'}</span>
                  <span>{t('auth.passwordRequirement.minLength')}</span>
                </div>
                <div className={`flex items-center gap-2 ${passwordRequirements.hasUpperCase ? (isDark ? 'text-emerald-400' : 'text-green-600') : ''}`}>
                  <span>{passwordRequirements.hasUpperCase ? '?' : '?'}</span>
                  <span>{t('auth.passwordRequirement.hasUpperCase')}</span>
                </div>
                <div className={`flex items-center gap-2 ${passwordRequirements.hasNumber ? (isDark ? 'text-emerald-400' : 'text-green-600') : ''}`}>
                  <span>{passwordRequirements.hasNumber ? '?' : '?'}</span>
                  <span>{t('auth.passwordRequirement.hasNumber')}</span>
                </div>
                <div className={`flex items-center gap-2 ${passwordRequirements.hasSpecialChar ? (isDark ? 'text-emerald-400' : 'text-green-600') : ''}`}>
                  <span>{passwordRequirements.hasSpecialChar ? '?' : '?'}</span>
                  <span>{t('auth.passwordRequirement.hasSpecialChar')}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Confirmar Senha */}
        <div>
          <label htmlFor="confirmPassword" className={`mb-2 block text-sm font-medium ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>
            {t('auth.confirmPassword')}
          </label>
          <div className='relative'>
            <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
              <LockIcon />
            </div>
            <input 
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'} 
              placeholder={t('auth.passwordPlaceholder')} 
              value={confirmPassword} 
              onChange={(e) => setConfirmPassword(e.target.value)} 
              className={INPUT_WITH_BOTH_ICONS_CLASS} 
              disabled={isLoading}
              required
            />
            <button
              type='button'
              className='absolute inset-y-0 right-0 flex items-center pr-3'
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? t('auth.hidePassword') : t('auth.showPassword')}
            >
              {showConfirmPassword ? (
                <EyeOffIcon className={`h-5 w-5 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
              ) : (
                <EyeIcon className={`h-5 w-5 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
              )}
            </button>
          </div>
          {confirmPassword && password !== confirmPassword && (
            <p className='mt-1 text-xs text-red-500'>{t('auth.errorPasswordMismatch')}</p>
          )}
        </div>
        
        <Button type='submit' fullWidth loading={isLoading} disabled={!passwordRequirements.minLength || password !== confirmPassword}>
          {t('auth.signupButton')}
        </Button>
      </form>
      
      <p className={`mt-6 text-center text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
        {t('auth.hasAccount')} <Link to={routes.login} className='font-semibold text-green-600 hover:text-green-700'>{t('auth.loginLink')}</Link>
      </p>
    </AuthLayout>
  )
}
